import React, { useEffect, useMemo } from "react";
import { Select } from "@pd/pd-ui-kit";
import { Control, Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetEmployeesForSelect } from "~/entity/Employee";
import { isAssignPositionFilled } from "~/features/LicenseActionsModals/model/mapPayload";
import { parseOwnerValue } from "~/features/LicenseActionsModals/model/owners";
import {
  parsePositionValue,
  useLicensePositionOptions,
} from "~/features/LicenseActionsModals/model/positions";
import {
  IAssignPositionValue,
  ILicenseAssignFormValues,
  ILicenseSelectOption,
} from "~/features/LicenseActionsModals/model/types";
import { withoutTakenOptions } from "~/features/LicenseActionsModals/model/withoutTakenOptions";
import { handlePopupScroll } from "~/shared/lib/handlePopupScroll";

import AddEntityLink from "./AddEntityLink";

import s from "~/features/LicenseActionsModals/LicenseActionsModals.less";

interface AssignPositionRowProps {
  control: Control<ILicenseAssignFormValues>;
  organizationIndex: number;
  positionIndex: number;
  ownerValue: string | null;
  value: IAssignPositionValue;
  isLast: boolean;
  takenPositionValues: Array<string | null | undefined>;
  onAddPosition: () => void;
}

const AssignPositionRow = ({
  control,
  organizationIndex,
  positionIndex,
  ownerValue,
  value,
  isLast,
  takenPositionValues,
  onAddPosition,
}: AssignPositionRowProps) => {
  const { t } = useTranslation();
  const fieldName = `organizations.${organizationIndex}.positions.${positionIndex}`;

  const { options: positionOptions, loading: positionsLoading } =
    useLicensePositionOptions(ownerValue);

  const visiblePositionOptions = useMemo(
    () =>
      withoutTakenOptions(
        positionOptions,
        takenPositionValues,
        value.position?.value,
      ),
    [positionOptions, takenPositionValues, value.position?.value],
  );

  const positionTarget = value.position?.value
    ? parsePositionValue(value.position.value)
    : null;
  const owner = ownerValue ? parseOwnerValue(ownerValue) : null;

  const employeePositionId = positionTarget?.positionInstanceId ?? null;
  const hasOwner = Boolean(owner?.dealerId || owner?.organizationId);

  const employeesEnabled =
    Boolean(positionTarget) && employeePositionId != null && hasOwner;

  const {
    options: employeeApiOptions,
    loading: employeesLoading,
    onSearch: onEmployeeSearch,
    onScrollBottom: onEmployeesScrollBottom,
    onOpenChange: onEmployeesOpenChange,
  } = useGetEmployeesForSelect(employeesEnabled, {
    dealerId: owner?.dealerId,
    partnerId: owner?.organizationId,
    positionInstanceIds: employeePositionId != null ? [employeePositionId] : [],
  });

  const {
    fields: employeeFields,
    append: appendEmployee,
    remove: removeEmployees,
  } = useFieldArray({
    control,
    name: `${fieldName}.employees`,
  });

  useEffect(() => {
    if (employeeFields.length === 0) {
      appendEmployee({ employee: null });
    }
  }, [employeeFields.length, appendEmployee]);

  useEffect(() => {
    if (employeesEnabled) {
      onEmployeesOpenChange(true);
    }
  }, [employeesEnabled, onEmployeesOpenChange]);

  const employeeOptions = useMemo(() => {
    const selected = (value.employees || [])
      .map((item) => item.employee)
      .filter(Boolean) as ILicenseSelectOption[];

    const missing = selected.filter(
      (item) =>
        !employeeApiOptions.some((option) => option.value === item.value),
    );

    return [...employeeApiOptions, ...missing];
  }, [employeeApiOptions, value.employees]);

  const takenEmployeeValues = (value.employees ?? []).map(
    (item) => item.employee?.value,
  );

  const lastEmployee = (value.employees ?? [])[
    (value.employees ?? []).length - 1
  ];
  const canAddEmployee = Boolean(lastEmployee && lastEmployee.employee);

  return (
    <div className={s.positionRow}>
      <div className={s.positionCol}>
        <div className={s.field}>
          <span className={s.label}>{t("licenseActionsModals.position")}</span>
          <Controller
            name={`${fieldName}.position`}
            control={control}
            defaultValue={value.position}
            render={({ onChange, value: selectValue }) => (
              <Select
                labelInValue
                showSearch
                optionFilterProp="label"
                value={selectValue}
                disabled={!ownerValue}
                onChange={(option) => {
                  onChange(option);
                  removeEmployees();
                  appendEmployee({ employee: null });
                }}
                options={visiblePositionOptions}
                loading={positionsLoading}
                placeholder={t("licenseActionsModals.selectPlaceholder")}
              />
            )}
          />
        </div>
        {isLast && (
          <div className={s.linkWrap}>
            <AddEntityLink
              disabled={!isAssignPositionFilled(value)}
              onClick={onAddPosition}
            >
              {t("licenseActionsModals.addPosition")}
            </AddEntityLink>
          </div>
        )}
      </div>
      <div className={s.employeeCol}>
        <span className={s.label}>{t("licenseActionsModals.employee")}</span>
        {employeeFields.map((employeeField, employeeIndex) => (
          <Controller
            key={employeeField.id}
            name={`${fieldName}.employees.${employeeIndex}.employee`}
            control={control}
            defaultValue={employeeField.employee}
            render={({ onChange, value: selectValue }) => (
              <Select
                labelInValue
                showSearch
                allowClear
                filterOption={false}
                value={selectValue}
                disabled={!positionTarget || !hasOwner}
                onChange={onChange}
                options={withoutTakenOptions(
                  employeeOptions,
                  takenEmployeeValues,
                  selectValue?.value,
                )}
                loading={employeesLoading}
                onSearch={onEmployeeSearch}
                onOpenChange={onEmployeesOpenChange}
                onPopupScroll={(event) =>
                  handlePopupScroll(event, onEmployeesScrollBottom)
                }
                placeholder={t("licenseActionsModals.selectPlaceholder")}
              />
            )}
          />
        ))}
        <div className={s.linkWrap}>
          <AddEntityLink
            disabled={!canAddEmployee}
            onClick={() => appendEmployee({ employee: null })}
          >
            {t("licenseActionsModals.addEmployee")}
          </AddEntityLink>
        </div>
      </div>
    </div>
  );
};

export default AssignPositionRow;
